import { Link, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { DirectoryView } from "./directory/DirectoryView";

/**
 * Expert directory area (route: /experts). The old app has **no standalone
 * experts tab** — the directory is reached only contextually from an action in
 * the action plan (the "expert" icon on a track-step action links here with
 * `?action=<id>`). Without an action there is nothing to browse, so we show
 * guidance back to the action plan rather than a browsable landing.
 *
 * The self-registration and expert-dashboard surfaces are deferred (findings
 * H-1/H-2/H-3) and intentionally not mounted here — there is no expert tab
 * anywhere in the SPA.
 */
export function ExpertArea() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as {
    action?: unknown;
    fav?: unknown;
  };
  const action = typeof search.action === "string" ? search.action : null;
  const favoritesDefault = search.fav === true;

  // Entered without an action context: the directory has nothing to load, so
  // point the user back to the action plan (the only legitimate entry point).
  if (!action) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card>
          <CardHeader>
            <CardTitle>{t("expert.directory.actionRequired.title")}</CardTitle>
            <CardDescription>
              {t("expert.directory.actionRequired.help")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild>
              <Link to="/action-plans">
                {t("expert.directory.actionRequired.goToPlans")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-3xl font-bold">{t("expert.title")}</h1>
        <p className="text-muted-foreground">{t("expert.subtitle")}</p>
      </header>

      <DirectoryView action={action} favoritesDefault={favoritesDefault} />
    </div>
  );
}
